"use client";

import React, { useState } from "react";
import { Briefcase, Building, Dumbbell, Baby, Building2, Key, ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const servicesData = [{
  title: "Office Cleaning",
  icon: Briefcase,
  shortDesc: "Maintain a pristine professional environment.",
  fullDesc: "Our comprehensive office cleaning ensures your workspace is spotless, hygienic, and conducive to productivity. We cover everything from workstations to boardrooms.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/office_cleaning_interior.png",
  link: "/ContactUsPage"
}, {
  title: "Strata Cleaning",
  icon: Building,
  shortDesc: "Reliable upkeep for residential and commercial complexes.",
  fullDesc: "We provide consistent maintenance for common areas, foyers, lifts, and exteriors, ensuring your strata property retains its value and appeal.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/strata_building_exterior.png",
  link: "/ContactUsPage"
}, {
  title: "Gym Cleaning",
  icon: Dumbbell,
  shortDesc: "Hygiene-focused cleaning for fitness centers.",
  fullDesc: "Protect your members with hospital-grade disinfection of equipment, change rooms, and high-touch surfaces to prevent the spread of germs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/professional_gym_sanitization_service.png",
  link: "/ContactUsPage"
}, {
  title: "Childcare Cleaning",
  icon: Baby,
  shortDesc: "Safe, non-toxic cleaning for little ones.",
  fullDesc: "We use child-safe, eco-friendly products to deep clean play areas, sleeping rooms, and amenities, meeting strict health and safety standards.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/childcare_play_area_clean.png",
  link: "/ContactUsPage"
}, {
  title: "Pressure Washing",
  icon: Building2,
  shortDesc: "Comprehensive pressure washing for carports, houses, driveways and more.",
  fullDesc: "From retail showrooms to industrial warehouses, our team delivers customized cleaning plans that align with your business hours and operational needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/industrial_pressure_washing_pavement.png",
  link: "/ContactUsPage"
}, {
  title: "End of Lease",
  icon: Key,
  shortDesc: "Bond-back guarantee cleaning services.",
  fullDesc: "Ensure a smooth handover with our detailed end-of-lease cleaning. We leave no corner untouched, helping you secure your bond return effortlessly.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1568801/img/residential_home_interior_clean.png",
  link: "/ContactUsPage"
}];
function ServiceCard({
  service,
  index
}: {
  service: typeof servicesData[0];
  index: number;
}) {
  const [isOpen, setIsOpen] = useState(false);
  return <div data-index={index} className="group relative h-full" onMouseEnter={() => setIsOpen(true)} onMouseLeave={() => setIsOpen(false)}>
      <Card className="relative h-full overflow-hidden border-0 bg-transparent shadow-xl transition-all duration-500 hover:shadow-2xl hover:shadow-primary/10">
        {}
        <div className="absolute inset-0 z-0">
          <div className="h-full w-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110" style={{
          backgroundImage: `url(${service.image})`
        }} />
          {}
          <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/70 to-black/30 transition-opacity duration-500 group-hover:via-black/80" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary/20 via-transparent to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        </div>

        {}
        <div className="relative z-10 flex h-full flex-col justify-end p-6">
          <CardHeader className="p-0 pb-2">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary backdrop-blur-sm transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
              <service.icon className="h-6 w-6" />
            </div>
            <CardTitle className="text-2xl font-bold text-white group-hover:text-primary transition-colors">
              {service.title}
            </CardTitle>
            <CardDescription className="text-gray-300 font-medium">
              {service.shortDesc}
            </CardDescription>
          </CardHeader>

          <Collapsible open={isOpen} className="space-y-4">
            <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
              <CardContent className="p-0 pt-2">
                <p className="mb-6 text-sm leading-relaxed text-gray-400">
                  {service.fullDesc}
                </p>
                <Button asChild className="w-full bg-primary text-primary-foreground hover:bg-primary/90 border-none">
                  <Link to={service.link}>
                    Book Now <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </CardContent>
            </CollapsibleContent>
          </Collapsible>
          
          {}
          <div className={`h-1 w-12 rounded-full bg-primary transition-all duration-300 ${isOpen ? 'w-full opacity-100' : 'opacity-0'}`} />
        </div>
      </Card>
    </div>;
}
export default function OurServices() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  return <section id="our-services" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 translate-x-1/2 translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />

      <div className="container mx-auto px-4 md:px-6">
        <div ref={ref} className={`mb-16 text-center transition-all duration-700 ease-out ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-1.5 text-sm font-medium text-primary mb-6">
            <Sparkles className="h-4 w-4" />
            <span>Premium Commercial Cleaning</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-white mb-6">
            Our <span className="text-primary">Services</span>
          </h2>
          <p className="mx-auto max-w-2xl text-lg text-muted-foreground">
            We provide reliable, high-quality cleaning services tailored to the unique needs of businesses across Newcastle, the Central Coast, and Sydney.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {servicesData.map((service, index) => <ServiceCard key={index} service={service} index={index} />)}
        </div>

        <div className="mt-16 text-center">
          <p className="text-muted-foreground mb-6">
            Need a custom cleaning solution for your business?
          </p>
          <Button asChild size="lg" variant="outline" className="border-primary/50 text-primary hover:bg-primary hover:text-primary-foreground">
            <Link to="/ContactUsPage">
              Get a Custom Quote
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}